import { NoSSR } from '@ui/components/NoSSR';
import { getPairMaxLeverage } from '@ui/features/MassiveToken/utils/getPairMaxLeverage';
import { useOrderType } from '@ui/features/Trade/components/TradeModule/store/useOrderType';
import { TradeModuleProvider } from '@ui/features/Trade/components/TradeModule/TradeModuleCtxProvider';
import { TradePairPanel } from '@ui/features/Trade/components/TradePairPanel';
import { useHasUnlockDegenLeverage } from '@ui/features/Trade/hooks/useHasUnlockDegenLeverage';
import { useTradeAnnouncementQueue } from '@ui/features/Trade/hooks/useTradeAnnouncementQueue';
import { useClosingPositionsStore } from '@ui/features/Trade/store/useClosingPositionsStore';
import {
  useLeverage,
  useLeverageValue,
} from '@ui/features/Trade/store/useLeverage';
import {
  useGetBaseTradePair,
  useIsBasePairSupportDegen,
} from '@ui/features/Trade/store/useTradingPair';
import { useRemoveOrderPendingData } from '@ui/features/Websocket/useRemoveOrderPendingData';
import { useRemoveTradePendingData } from '@ui/features/Websocket/useRemoveTradePendingData';
import { useSubscribeTradeEvents } from '@ui/features/Websocket/useSubscribeTradeEvents';
import { usePositions } from '@ui/hooks/trade/usePositions';
import { OrderType } from '@ui/types/trade';
import type { NextPage } from 'next';
import { useEffect } from 'react';
import { Box, Flex } from 'styled-system/jsx';

import { CakepadLeverupBanner } from './CakepadLeverupBanner';
import { Trade } from './Trade';

export const Page: NextPage = () => {
  return (
    <Flex
      direction={{
        base: 'column',
        desktop: 'row',
      }}
    >
      <TradeModuleProvider>
        {/* <Box
          display={{
            base: 'block',
            desktop: 'none',
          }}
        >
          <CakepadLeverupBanner />
        </Box> */}
        <TradePairPanel />
        <Trade
          flex={1}
          display={{
            base: 'flex',
            desktop: 'flex',
          }}
        />
        <Hooks />
      </TradeModuleProvider>
    </Flex>
  );
};

const Hooks = () => {
  useLeverageValueCheck();

  // useSubscribeTradeEvents();
  // useRemoveTradePendingData();
  useRemoveOrderPendingData();

  return null;
};

const useLeverageValueCheck = () => {
  // only use in leverage / trade relative page, currently only in Trade page
  const leverage = useLeverage();

  const isBasePairSupportDegen = useIsBasePairSupportDegen();
  const hasUnlockDegenLeverage = useHasUnlockDegenLeverage();

  const orderType = useOrderType((s) => s._persistValue);

  const basePair = useGetBaseTradePair();
  const maxStandardLeverage = getPairMaxLeverage({ tradePair: basePair });

  useEffect(() => {
    const setLeverageToStandardMax = () => {
      if (maxStandardLeverage) {
        useLeverageValue.getState().setPersistValue(maxStandardLeverage);
      }
    };

    if (
      !isBasePairSupportDegen &&
      maxStandardLeverage &&
      leverage > maxStandardLeverage
    ) {
      setLeverageToStandardMax();
    }

    if (
      isBasePairSupportDegen &&
      !hasUnlockDegenLeverage &&
      maxStandardLeverage &&
      leverage > maxStandardLeverage
    ) {
      setLeverageToStandardMax();
    }

    if (
      orderType === OrderType.LIMIT &&
      maxStandardLeverage &&
      leverage > maxStandardLeverage
    ) {
      setLeverageToStandardMax();
    }

    return () => { };
  }, [
    hasUnlockDegenLeverage,
    isBasePairSupportDegen,
    leverage,
    maxStandardLeverage,
    orderType,
  ]);
};

const useRemoveClosingPositionId = () => {
  const { data: positions } = usePositions();
  const closingPositionIds = useClosingPositionsStore(
    (s) => s.closingPositionIds,
  );
  const removeClosedPosition = useClosingPositionsStore(
    (s) => s.removeClosedPosition,
  );

  useEffect(() => {
    if (positions && closingPositionIds.length > 0) {
      closingPositionIds.forEach((closingPosition) => {
        const positionExists = positions.some(
          (position) =>
            position.id === closingPosition.id &&
            (closingPosition.positionTimestamp === undefined ||
              position.timestamp === closingPosition.positionTimestamp),
        );
        if (!positionExists) {
          removeClosedPosition(
            closingPosition.id,
            closingPosition.positionTimestamp,
          );
        }
      });
    }

    return () => { };
  }, [closingPositionIds, positions, removeClosedPosition]);
};